import { cn } from '../../../components/ui/utils';

export interface HeatmapRow {
  key: string;
  label: string;
  values: (number | null)[];
  unknown?: number;
}

export function HrHeatmap({
  columns,
  rows,
  formatter = (value) => String(value),
  onCellClick,
  activeCell,
  unknownLabel = 'Unknown',
}: {
  columns: string[];
  rows: HeatmapRow[];
  formatter?: (value: number, rowLabel: string, columnLabel: string) => string;
  onCellClick?: (rowKey: string, columnLabel: string) => void;
  activeCell?: { rowKey: string; columnLabel: string } | null;
  unknownLabel?: string;
}) {
  const max = Math.max(
    1,
    ...rows.flatMap((row) => row.values.map((value) => value || 0))
  );

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted">
            <th className="px-3 py-2 text-left font-semibold text-foreground">Department</th>
            {columns.map((column) => (
              <th
                key={column}
                className="px-2 py-2 text-center text-xs font-semibold text-foreground"
              >
                {column}
              </th>
            ))}
            <th className="px-3 py-2 text-right text-xs font-semibold text-foreground">
              {unknownLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-border last:border-0">
              <td className="px-3 py-2 whitespace-nowrap text-foreground">{row.label}</td>
              {row.values.map((value, index) => {
                const column = columns[index];
                const numeric = value || 0;
                const intensity = Math.round((numeric / max) * 55);
                const active =
                  activeCell?.rowKey === row.key && activeCell?.columnLabel === column;

                return (
                  <td key={column} className="p-0.5">
                    <button
                      type="button"
                      onClick={() => onCellClick?.(row.key, column)}
                      disabled={!onCellClick}
                      title={`${row.label} · ${column}: ${formatter(numeric, row.label, column)}`}
                      className={cn(
                        'h-9 w-full min-w-[52px] rounded-sm text-center text-xs font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        numeric === 0 && 'text-muted-foreground',
                        onCellClick && 'cursor-pointer hover:ring-1 hover:ring-ring',
                        active && 'ring-2 ring-primary'
                      )}
                      style={{
                        backgroundColor: numeric
                          ? `color-mix(in srgb, var(--chart-3) ${Math.max(10, intensity)}%, transparent)`
                          : undefined,
                      }}
                    >
                      {numeric ? formatter(numeric, row.label, column) : '·'}
                    </button>
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                {row.unknown || 0}
              </td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + 2}
                className="px-3 py-8 text-center text-sm text-muted-foreground"
              >
                No records in the current filter
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
